import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import { useDebounce } from '../../redux/hooks';
import {
  Wrapper,
  InputWrapper,
  CloseButton,
  SearchInput,
  Menu,
  Item,
  Loader,
  MenuWrapper,
} from './styles';
import { SvgChevron, SvgLoader } from '../../svg';
import { useTheme } from '../../theme';
import emptyStateImg from '../../assets/images/empty_state_research.png'
import { Paragraph } from '../../typography';
import { useTranslate } from '@federico.moretti/react-translate';

const defaultItemToString = (i) => i;

function AsyncSelect(props) {
  const {
    api,
    itemToString: oldItemToString = defaultItemToString,
    onChange,
    onInputValueChange = () => {},
    onTouch = () => {},
    initialSelectedItem,
    initialInputValue = '',
    placeholder = 'Select...',
    style,
    testid = 'async-select',
    id,
    clearInputOnChange,
    disabled,
  } = props;

  const theme = useTheme();

  const [data, setData] = React.useState([]);
  const [noResults, setNoResults] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const inputRef = React.useRef();
  const isInputFocused = () => document.activeElement === inputRef.current;

  const itemToString = (v) => {
    if (v != null) {
      return oldItemToString(v);
    }
    return '';
  };

  function stateReducer(state, actionAndChanges) {
    switch (actionAndChanges.type) {
      case useCombobox.stateChangeTypes.InputChange:
        return {
          ...actionAndChanges.changes,
          selectedItem: actionAndChanges.changes.inputValue === '' ? null : state.selectedItem,
        };
      default:
        return actionAndChanges.changes;
    }
  }

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
    selectedItem,
  } = useCombobox({
    onSelectedItemChange: (changes) => {
      // FIXME: https://github.com/downshift-js/downshift/issues/874
      if (changes.selectedItem) {
        setTimeout(() => onChange(changes.selectedItem));
        // onChange(changes.selectedItem);
        if (clearInputOnChange) {
          setInputValue('');
        }
      }
    },
    onInputValueChange: (changes) => {
      if (changes.inputValue) {
        setTimeout(() => onInputValueChange(changes.inputValue));
      }
    },
    initialSelectedItem,
    initialInputValue,
    items: data,
    itemToString,
    stateReducer,
    disabled,
  });
  const {t} = useTranslate()
  const toggleButtonRef = React.useRef();

  const debouncedInputValue = useDebounce(inputValue, 500);
  React.useEffect(() => {
    let ignore = false;

    async function fetchApi(value) {
      const res = await api(value);
      if (!ignore) {
        if (res?.length === 0) {
          setNoResults(true);
        }
        setData(res);
        setIsLoading(false);
      }
    }

    if (api && debouncedInputValue !== '') {
      setIsLoading(true);
      fetchApi(debouncedInputValue);
    }

    return () => {
      ignore = true;
      setNoResults(false)
    };
  }, [api, debouncedInputValue]);

  useEffect(() => {
    if (inputValue?.trim() === '' && isOpen) {
      toggleButtonRef?.current?.click();
    }
  }, [inputValue, isOpen])


  return (
    <>
      <button type='button' style={{display:'none'}} {...getToggleButtonProps({ ref: toggleButtonRef })}/>
      <Wrapper
        disabled={disabled}
        isFocused={isInputFocused()}
        isMenuOpen={isOpen}
        {...getComboboxProps({
          onClick: () => {
            inputRef.current.focus();
          },
        })}
        style={style}
      >
        <InputWrapper>
          <SearchInput
            id={id}
            data-testid={`${testid}-input`}
            onFocus={onTouch}
            visuallyHidden={false}
            placeholder={placeholder}
            style={{ width: '100%' }}
            {...getInputProps({ ref: inputRef, disabled })}
          />
          {isLoading ? (
            <Loader>
              <SvgLoader stroke={theme.colors.primary} />
            </Loader>
          ) : null}
        </InputWrapper>
        <MenuWrapper {...getMenuProps()} data-testid={`${testid}-menu`}>
          {isOpen ? (
            <Menu>
              {data?.length > 0 ?
                data.map((item, index) => (
                  <Item
                    data-testid={`${testid}-item-${index}`}
                    highlighted={highlightedIndex === index}
                    bold={itemToString(item) === itemToString(selectedItem)}
                    key={index}
                    {...getItemProps({
                      item,
                      index,
                    })}
                  >
                    {itemToString(item)}
                  </Item>
                )) : noResults ? <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '250px',
                }}>
                  <img src={emptyStateImg} alt="" />
                  <Paragraph style={{ color: '#6A6A84' }}>{t('noInstrumentsFound')}</Paragraph>
                </div> : null}
            </Menu>
          ) : null}
        </MenuWrapper>
      </Wrapper>
    </>
  );
}

AsyncSelect.propTypes = {
  api: PropTypes.func.isRequired,
  itemToString: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func,
  onTouch: PropTypes.func,
  initialSelectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  initialInputValue: PropTypes.string,
  placeholder: PropTypes.string,
  clearInputOnChange: PropTypes.bool,
  testid: PropTypes.string,
  id: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
};

export default AsyncSelect;
